import {
  Box,
  Button,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';

const ZOOM_LEVELS = [50, 75, 90, 100, 110, 125, 150, 175, 200];
const ZOOM_MIN = 50;
const ZOOM_MAX = 200;
const ZOOM_STEP = 10;
const STORAGE_KEY = 'app-zoom-level';

function applyZoomToDOM(level: number) {
  const scale = level / 100;
  const container = document.querySelector<HTMLElement>(
    '.main-content-container'
  );
  if (!container) return;

  if (level === 100) {
    container.style.transform = '';
    container.style.width = '';
    container.style.height = '';
  } else {
    container.style.transform = `scale(${scale})`;
    container.style.transformOrigin = 'top left';
    container.style.width = `${100 / scale}%`;
    container.style.height = `${100 / scale}%`;
  }

  localStorage.setItem(STORAGE_KEY, level.toString());
}

interface ZoomControlProps {
  version?: string;
}

export const ZoomControl = ({ version }: ZoomControlProps) => {
  const [zoomLevel, setZoomLevel] = React.useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : 100;
  });

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const applyZoom = React.useCallback((level: number) => {
    applyZoomToDOM(level);
    setZoomLevel(level);
  }, []);

  // aplica zoom salvo na montagem
  React.useEffect(() => {
    if (zoomLevel !== 100) applyZoomToDOM(zoomLevel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleZoomIn = React.useCallback(
    () => applyZoom(Math.min(zoomLevel + ZOOM_STEP, ZOOM_MAX)),
    [applyZoom, zoomLevel]
  );
  const handleZoomOut = React.useCallback(
    () => applyZoom(Math.max(zoomLevel - ZOOM_STEP, ZOOM_MIN)),
    [applyZoom, zoomLevel]
  );
  const handleZoomReset = React.useCallback(() => applyZoom(100), [applyZoom]);

  // atalhos de teclado Ctrl+/Ctrl-/Ctrl+0
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        handleZoomIn();
      } else if (e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        handleZoomReset();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [handleZoomIn, handleZoomOut, handleZoomReset]);

  return (
    <Box
      sx={{
        display: { xs: 'none', sm: 'flex' },
        alignItems: 'center',
        pr: 1.5,
      }}
    >
      {/* versão */}
      {version && (
        <Box
          sx={{
            mr: 1.5,
            display: { md: 'flex', xs: 'none' },
            alignItems: 'baseline',
            gap: 0.5,
          }}
        >
          <Typography
            component="a"
            // href="/about/info"
            sx={{
              fontSize: 12,
              color: '#fff',
              textDecoration: 'none',
              '&:hover': { color: '#fff' },
            }}
          >
            versão:
          </Typography>
          <Typography
            component="a"
            // href="/about/info"
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: '#fff',
              textDecoration: 'none',
              '&:hover': { color: '#fff' },
            }}
          >
            {version}
          </Typography>
        </Box>
      )}

      {/* botão de zoom */}
      <Tooltip title={`Zoom: ${zoomLevel}% (Ctrl+/Ctrl-/Ctrl+0)`}>
        <Button
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
          sx={{
            ml: 1,
            color: '#fff',
            '&:hover': { color: '#fff', backgroundColor: 'transparent' },
          }}
        >
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
            +
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', px: 0.7 }}>
            {zoomLevel}%
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>−</Typography>
        </Button>
      </Tooltip>

      {/* menu de níveis */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          // list: { sx: { py: 1 } },
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              border: '1px solid #e5e7eb',
              borderRadius: 2,
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
              mt: 1.5,
              minWidth: 120,
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 8,
                height: 8,
                bgcolor: 'background.paper',
                border: '1px solid #e5e7eb',
                borderRight: 'none',
                borderBottom: 'none',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        {ZOOM_LEVELS.map((level) => (
          <MenuItem
            key={level}
            selected={level === zoomLevel}
            onClick={() => applyZoom(level)}
            sx={{
              py: 0.75,
              px: 2,
              minHeight: 'auto',
              justifyContent: 'center',
              '&:hover': { backgroundColor: '#f9fafb' },
              '&.Mui-selected': {
                backgroundColor: '#f3f4f6',
                '&:hover': { backgroundColor: '#e5e7eb' },
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: 13,
                fontWeight: level === zoomLevel ? 600 : 400,
                color: '#374151',
              }}
            >
              {level}%
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default ZoomControl;
